/**
 * MAGCORE implementation of SMB/CIFS network plugin.
 *
 * There are different realizations of core SMB API -- old synchronous API and new asynchronous,
 * with callback-based asynchronous model. Plugin realization supports two variations for backward compatibility.
 *
 * @author Yaroslav Surilov <y.surilov@infomir.com>
 */

'use strict';

var SMB_PATH      = '/ram/mnt/smb', // default dir for SAMBA mount point
    SMB_MOUNTED   = false,          // flag is a SAMBA share mounted to the SMB_PATH
    apiVersion    = +gSTB.Version().split(';')[0].split(':')[1].trim(),
    asyncApiAvail = false,
    /**
     * @namespace
     * @property {boolean} enabled enable/disable cache entirely
     * @property {string} activeGroup currently opened group
     * @property {string} activeServer currently opened server
     * @property {Object} data all cached resources: groups, servers and shares
     * @property {number} groupsCacheTime value in seconds of lifetime of SAMBA work groups cache
     * @property {number} serversCacheTime value in seconds of lifetime of SAMBA servers cache
     * @property {number} sharesCacheTime value in seconds of lifetime of SAMBA shares cache
     * @property {function} setup
     */
    cache = {
        enabled: true,
        workers: [],
        activeGroup: null,
        activeServer: null,
        /**
         * @example
         * {
         *     groups: [group_0, group_1, ...],
         *     servers: {
         *         group_0: [server_0, server_1, ...],
         *         group_1: [server_0, server_1, ...]
         *     },
         *     shares: {
         *         server_0: [share_0, share_1, ...],
         *         server_1: [share_0, share_1, ...]
         *     }
         * }
         */
        data: {
            groups: null,
            servers: null,
            shares: null
        },
        groupsCacheTime: 3600,
        serversCacheTime: 300,
        sharesCacheTime: 300,
        setup: function ( worker ) {
            if ( this.workers.indexOf(worker) === -1 ) {
                this.workers.push(worker);
            }

            worker.setupTime = new Date();
        },
        clear: function () {
            cache.data.groups = null;
            cache.data.servers = null;
            cache.data.shares = null;
            cache.activeGroup = null;
            cache.activeServer = null;

            cache.workers.forEach(function ( worker ) {
                delete worker.setupTime;
            });

            cache.workers = [];
        }
    };

// global object is presented and STB API version is appropriate
// go into new async realization
if ( window.stbSmb && apiVersion >= 342 ) {
    asyncApiAvail = true;
}


/**
 * Mount SAMBA shared directory.
 *
 * @param {Object} config mount options
 * @param {string} config.address ip address
 * @param {string} [config.login] needed login
 * @param {string} [config.password] needed password
 * @param {string} [config.mountPath] path to mount point
 */
function mountSmb ( config ) {
    var login,
        password,
        command,
        status;

    if ( config && config.address ) {
        config.address = config.address.charAt(config.address.length - 1) === '/' ?
            config.address.substr(0, config.address.length - 1) :
            config.address;
        login = config.login || 'guest';
        password = config.password ||  '';
        command = 'mount cifs "' + config.address + '" "' + (config.mountPath || SMB_PATH) + '" username=' + login +
            ',password=' + password + ',iocharset=utf8'; //,unc=' + config.address.replace(/\//g, '\\\\');
        status = gSTB.RDir(command).trim();
        SMB_MOUNTED = status === 'Ok';
    }
}


/**
 * Fetch available Samba work groups.
 * Realize polymorphic behaviour.
 *
 * @param {function} callback scan result handler
 * @param {boolean} useCache enable/disable caching for this worker
 */
function getGroups ( callback, useCache ) {
    var data, self, groups;

    if ( typeof callback !== 'function' ) {
        throw new Error('[Network]::getGroups()> callback must be a function');
    }

    useCache = typeof useCache === typeof true ? useCache : true;

    if ( cache.enabled && useCache ) {
        if ( !cache.data.groups ) {
            cache.setup(getGroups);
        } else if ( (new Date() - getGroups.setupTime) / 1000 > cache.groupsCacheTime ) {
            cache.setup(getGroups);
            // reset data
            cache.data.groups = null;
            cache.activeGroup = null;
        } else {
            callback(null, cache.data.groups);

            return;
        }
    }

    data = [];
    self = this; // eslint-disable-line


    /**
     * Iterate through SAMBA groups.
     *
     * @param {Object[]} groups retrieved SAMBA groups
     */
    function iterateGroups ( groups ) {
        groups.forEach(function ( group ) {
            if ( group ) {
                data.push({
                    name: group,
                    uri: group,
                    type: self.types.SAMBA_GROUP
                });
            }
        });

        callback(null, cache.data.groups = data);

        if ( cache.data.servers ) {
            Object.keys(cache.data.servers).forEach(function ( key ) {
                if ( groups.indexOf(key) === -1 ) {
                    delete cache.data.servers[key];
                }
            });
        }
    }


    if ( asyncApiAvail ) {
        window.stbSmb.findGroups(function ( error, groups ) {
            if ( error ) {
                callback(error);
            } else if ( Array.isArray(groups) ) {
                iterateGroups(groups);
            } else {
                callback({message: 'Invalid data format!'});
            }
        });
    } else {
        try {
            groups = JSON.parse(gSTB.GetSmbGroups());
        } catch ( error ) {
            callback(error);

            return;
        }

        if ( groups && Array.isArray(groups.result) ) {
            iterateGroups(groups.result);
        } else {
            callback({message: 'Invalid data format!'});
        }
    }
}


/**
 * Async fetch available servers for given work group.
 *
 * @param {Object} config scan configuration
 * @param {string} config.group SMB group to scan
 * @param {function} callback scan result handler
 * @param {boolean} useCache enable/disable caching for this worker
 */
function getServers ( config, callback, useCache ) {
    var data, self, servers;

    if ( typeof callback !== 'function' ) {
        throw new Error('[Network]::getServers()> callback must be a function');
    }

    useCache = typeof useCache === typeof true ? useCache : true;

    if ( cache.enabled && useCache ) {
        cache.activeGroup = config.group;

        if ( !cache.data.servers ) {
            cache.setup(getServers);
        } else if ( (new Date() - getServers.setupTime) / 1000 > cache.serversCacheTime ) {
            cache.setup(getServers);
            // reset data
            cache.data.servers = null;
            cache.activeServer = null;
        } else if ( cache.data.servers[cache.activeGroup] ) {
            callback(null, cache.data.servers[cache.activeGroup]);

            return;
        }
    }

    data = [];
    self = this; // eslint-disable-line


    /**
     * Iterate through SAMBA servers.
     *
     * @param {Object[]} servers retrieved SAMBA servers
     */
    function iterateServers ( servers ) {
        servers.forEach(function ( server ) {
            if ( server ) {
                data.push({
                    name: server,
                    uri: config.group + '/' + server,
                    type: self.types.SAMBA_HOST
                });
            }
        });

        cache.data.servers = cache.data.servers || {};
        cache.data.servers[cache.activeGroup] = data;
        callback(null, data);

        if ( cache.data.shares ) {
            Object.keys(cache.data.shares).forEach(function ( key ) {
                if ( servers.indexOf(key) === -1 ) {
                    delete cache.data.shares[key];
                }
            });
        }
    }


    if ( asyncApiAvail ) {
        // transform config accordingly to API version method signature
        window.stbSmb.findServers(apiVersion === 342 ? config.group : config, function ( error, servers ) {
            if ( error ) {
                callback(error);
            } else if ( Array.isArray(servers) ) {
                iterateServers(servers);
            } else {
                callback({message: 'Invalid data format!'});
            }
        });
    } else {
        try {
            servers = JSON.parse(gSTB.GetSmbServers(JSON.stringify(config)));
        } catch ( error ) {
            callback(error);

            return;
        }

        if ( servers && Array.isArray(servers.result) ) {
            iterateServers(servers.result);
        } else {
            callback({message: 'Invalid data format!'});
        }
    }
}


/**
 * Async fetch available Samba shares for the given server.
 *
 * @param {Object} config scan configuration
 * @param {string} config.server server name to scan
 * @param {string} [config.login=guest] server account login
 * @param {string} [config.password] server account password
 * @param {function} callback scan result handler
 * @param {boolean} useCache enable/disable caching for this worker
 */
function getShares ( config, callback, useCache ) {
    var data, shares;

    if ( typeof callback !== 'function' ) {
        throw new Error('[Network]::getShares()> callback must be a function');
    }

    useCache = typeof useCache === typeof true ? useCache : true;
    SMB_MOUNTED = false;

    if ( cache.enabled && useCache ) {
        cache.activeServer = config.server;

        if ( !cache.data.shares ) {
            cache.setup(getShares);
        } else if ( (new Date() - getShares.setupTime) / 1000 > cache.sharesCacheTime ) {
            cache.setup(getShares);
            cache.data.shares = null;
        } else if ( cache.data.shares[cache.activeServer] ) {
            callback(null, cache.data.shares[cache.activeServer]);

            return;
        }
    }

    data = [];


    /**
     * Iterate through SAMBA shares.
     *
     * @param {Object[]} shares list of SAMBA shares
     * @param {*} error occurred error
     * @param {Object[]} ips list of resolved IP addresses
     */
    function iterateShares ( shares, error, ips ) {
        if ( error ) {
            callback(error);
        } else {
            shares.forEach(function ( share ) {
                if ( share ) {
                    data.push({
                        name: share,
                        //address: '//' + server + '/' + share,
                        addresses: ips
                        //server: server
                        //type: self.types.SAMBA_SHARE
                    });
                }
            });

            cache.data.shares = cache.data.shares || {};
            cache.data.shares[cache.activeServer] = data;
            callback(null, data);
        }
    }


    if ( asyncApiAvail ) {
        if ( apiVersion === 342 ) {
            window.stbSmb.findShares('""', config.server, function ( error, shares ) {
                if ( error ) {
                    callback(error);
                } else if ( Array.isArray(shares) ) {
                    window.stbSmb.findIp('""', config.server, function ( error, ips ) {
                        iterateShares(shares, error, ips);
                    });
                } else {
                    callback({message: 'Invalid data format!'});
                }
            });
        } else {
            window.stbSmb.findShares(config, function ( error, shares ) {
                if ( error ) {
                    callback(error);
                } else if ( Array.isArray(shares) ) {
                    window.stbSmb.findIps({server: config.server}, function ( error, ips ) {
                        iterateShares(shares, error, ips);
                    });
                } else {
                    callback({message: 'Invalid data format!'});
                }
            });
        }
    } else {
        try {
            shares = JSON.parse(gSTB.GetSmbShares(JSON.stringify(config)));
        } catch ( error ) {
            callback(error);

            return;
        }

        if ( shares && shares.result && Array.isArray(shares.result.shares) ) {
            shares.result.shares.forEach(function ( share ) {
                if ( share ) {
                    data.push({
                        name: share,
                        addresses: [shares.result.serverIP].concat(shares.result.additionalIPs || [])
                        //type: self.types.SAMBA_SHARE
                    });
                }
            });

            cache.data.shares = cache.data.shares || {};
            cache.data.shares[cache.activeServer] = data;
            callback(null, data);
        } else {
            callback({message: 'Invalid data format!'});
        }
    }
}


/**
 * Open particular SMB share.
 *
 * @param {Object} config opener configuration
 * @param {function} callback scan result handler
 */
function openShare ( config, callback ) {
    var idx = 0,
        addresses = config.addresses;

    while ( idx < addresses.length ) {
        mountSmb({address: '//' + addresses[idx] + '/' + config.folder, login: config.login, password: config.password});

        if ( SMB_MOUNTED ) {
            break;
        }

        idx += 1;
    }

    setTimeout(function () {
        callback(null, SMB_MOUNTED);
    }, 0);
}


module.exports = {
    onAppInit: function ( config, callback ) {
        var api = {
            config: config.app,
            types: {
                SAMBA_GROUP: 0,
                SAMBA_HOST: 1,
                SAMBA_SHARE: 2
            },
            clearCache: cache.clear,
            getGroups: getGroups,
            getServers: getServers,
            getShares: getShares,
            openShare: openShare
        };

        // dome some asynchronous operations
        callback(null, api);
    }
};
